import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import api from '../../services/api';
import GoogleMapsLoader from '../GoogleMapsLoader';
import PlacesAutocomplete from '../PlacesAutocomplete';
import FarmAvatarCreator from '../FarmAvatarCreator';

interface FarmForm {
  name: string;
  description: string;
  address: {
    formatted: string;
    street?: string;
    city?: string;
    state?: string;
    country?: string;
    zipCode?: string;
  };
  coordinates: [number, number];
  avatar: {
    type: 'shape' | 'image';
    data: string;
  };
}

interface Props {
  onComplete: () => void;
}

const StartWithFarm: React.FC<Props> = ({ onComplete }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedPlace, setSelectedPlace] = useState<google.maps.places.PlaceResult | null>(null);
  const [showAddressCorrection, setShowAddressCorrection] = useState(false);
  const [manualAddress, setManualAddress] = useState('');
  const [farmAvatar, setFarmAvatar] = useState<{ type: 'shape' | 'image'; data: string }>({ 
    type: 'shape', 
    data: 'circle:solid:#22c55e' 
  });

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<FarmForm>();

  const handlePlaceSelect = (place: google.maps.places.PlaceResult) => {
    setSelectedPlace(place);
    if (place.formatted_address) {
      setValue('address.formatted', place.formatted_address);
      setManualAddress(place.formatted_address);
    }

    // Extract address components
    if (place.address_components) {
      let street = '';
      let city = '';
      let state = '';
      let country = '';
      let zipCode = '';

      place.address_components.forEach((component) => {
        const types = component.types;
        if (types.includes('street_number')) {
          street = component.long_name + ' ';
        }
        if (types.includes('route')) {
          street += component.long_name;
        }
        if (types.includes('locality')) {
          city = component.long_name;
        }
        if (types.includes('administrative_area_level_1')) {
          state = component.short_name;
        }
        if (types.includes('country')) {
          country = component.long_name;
        }
        if (types.includes('postal_code')) {
          zipCode = component.long_name;
        }
      });

      setValue('address.street', street);
      setValue('address.city', city);
      setValue('address.state', state);
      setValue('address.country', country);
      setValue('address.zipCode', zipCode);
    }

    // Set coordinates
    if (place.geometry?.location) {
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      setValue('coordinates', [lng, lat]);
    }
  };

  const handleAddressCorrection = () => {
    setValue('address.formatted', manualAddress);
    setShowAddressCorrection(false);
  };

  const onSubmit = async (data: FarmForm) => {
    setLoading(true);
    setError('');

    try {
      await api.post('/onboarding/start-with-farm', {
        ...data,
        avatar: farmAvatar
      });
      toast.success('🚜 Your farm has been established!');
      onComplete();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create farm');
    } finally {
      setLoading(false);
    }
  };

  return (
    <GoogleMapsLoader>
      <Paper elevation={2} sx={{ p: 4 }}>
        <Box textAlign="center" mb={4}>
          <Typography variant="h2" sx={{ fontSize: '3rem', mb: 2 }}>
            🚜
          </Typography>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Establish Your Farm
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Set up a complete farm with precise location tracking
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Farm Avatar */}
          <Box mb={3}>
            <FarmAvatarCreator 
              onAvatarSelect={setFarmAvatar}
              currentAvatar={farmAvatar}
            />
          </Box>

          <TextField
            fullWidth
            label="Farm Name"
            margin="normal"
            {...register('name', {
              required: 'Please name your farm',
            })}
            error={!!errors.name}
            helperText={errors.name?.message || 'e.g., "Green Acres Farm", "Urban Harvest"'}
          />

          <TextField
            fullWidth
            label="Description"
            margin="normal"
            multiline
            rows={3}
            {...register('description', {
              required: 'Please describe your farm',
            })}
            error={!!errors.description}
            helperText={errors.description?.message || 'What do you grow? What makes your farm special?'}
          />

          <Controller
            name="address.formatted"
            control={control}
            rules={{ required: 'Please select your farm location' }}
            render={({ field }) => (
              <PlacesAutocomplete
                onPlaceSelect={handlePlaceSelect}
                label="Farm Location"
                placeholder="Start typing your farm address..."
                error={!!errors.address?.formatted}
                helperText={errors.address?.formatted?.message}
                value={field.value}
              />
            )}
          />

          {selectedPlace && (
            <Box mt={2} display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" color="text.secondary">
                Not quite right?
              </Typography>
              <Button
                size="small"
                onClick={() => setShowAddressCorrection(true)}
              >
                Correct Address
              </Button>
            </Box>
          )}

          <Box mt={4}>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Create My Farm'}
            </Button>
          </Box>
        </form>

        <Box mt={3} textAlign="center">
          <Typography variant="body2" color="text.secondary">
            💡 Tip: Your farm's exact location helps others discover and visit your gardens!
          </Typography>
        </Box>
      </Paper>

      {/* Address Correction Dialog */}
      <Dialog open={showAddressCorrection} onClose={() => setShowAddressCorrection(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Correct Farm Address</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Corrected Address"
            value={manualAddress}
            onChange={(e) => setManualAddress(e.target.value)}
            margin="normal"
            multiline
            rows={2}
            helperText="Edit the address if Google Maps didn't get it quite right"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddressCorrection(false)}>Cancel</Button>
          <Button onClick={handleAddressCorrection} variant="contained">
            Save Correction
          </Button>
        </DialogActions>
      </Dialog>
    </GoogleMapsLoader>
  );
};

export default StartWithFarm;